import { useEffect, useRef, useState } from "react";
import api, { API, fmtTRY } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkles, Send, Upload, FileImage, Loader2 } from "lucide-react";

export default function AI() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Merhaba 👋 Ben NakitFlow AI danışmanınız. Nakit akışı, bütçe önerisi veya anomaliler hakkında soru sorabilirsiniz." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const scrollRef = useRef();

  const loadReceipts = () => api.get("/receipts").then((r) => setReceipts(r.data));
  useEffect(() => { loadReceipts(); }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    try {
      const { data } = await api.post("/ai/chat", { message: q, session_id: session });
      setSession(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) { toast.error("AI yanıt vermedi"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const token = localStorage.getItem("nf_token");
      const r = await fetch(`${API}/receipts/upload`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Yükleme hatası");
      toast.success("Fiş yüklendi, OCR tamamlandı");
      loadReceipts();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const examples = [
    "Gelecek ay nakit akışım yeterli mi?",
    "En büyük gider kalemim ne, nasıl azaltabilirim?",
    "Bu ay bir anomali var mı?",
    "Yaklaşan ödemelerimi nasıl planlamalıyım?",
  ];

  return (
    <div className="space-y-5 nf-fade-up">
      <div>
        <div className="text-xs uppercase tracking-widest font-mono-num text-slate-500 flex items-center gap-2"><Sparkles className="w-3 h-3 text-purple-400" /> AI</div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Finans Asistanı & Fiş OCR</h1>
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* Chat */}
        <div className="lg:col-span-7 nf-card flex flex-col h-[70vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] p-3 rounded-xl text-sm leading-relaxed ${m.role === "user" ? "bg-cyan-500/15 border border-cyan-500/30 text-cyan-50" : "bg-slate-900/60 border border-slate-800 text-slate-200"}`}>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              </div>
            ))}
            {busy && <div className="flex gap-2 text-slate-500 text-xs items-center"><Loader2 className="w-3 h-3 animate-spin" /> AI düşünüyor...</div>}
          </div>
          <div className="p-3 border-t border-slate-800">
            <div className="flex gap-2 flex-wrap mb-2">
              {examples.map((q) => (
                <button key={q} onClick={() => setInput(q)} className="text-[11px] px-2.5 py-1 rounded-full bg-slate-800/60 border border-slate-700 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300">{q}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Finans sorunuzu yazın..." className="bg-slate-900/60 border-slate-800" />
              <Button data-testid="ai-send-btn" onClick={send} disabled={busy} className="bg-gradient-to-r from-cyan-500 to-purple-500 text-slate-950 font-semibold"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>

        {/* OCR */}
        <div className="lg:col-span-5 nf-card p-5">
          <h2 className="font-head text-lg font-bold mb-3 flex items-center gap-2"><FileImage className="w-4 h-4 text-emerald-400" /> Fiş/Fatura OCR</h2>
          <label data-testid="ocr-upload-area" className="block border-2 border-dashed border-slate-700 rounded-xl p-6 text-center hover:border-cyan-500/50 cursor-pointer bg-slate-900/30 transition">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
            {uploading ? (
              <div className="flex flex-col items-center gap-2 text-cyan-400">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-sm">AI okuyor...</span>
              </div>
            ) : (
              <>
                <Upload className="w-7 h-7 mx-auto text-slate-500 mb-2" />
                <div className="text-sm text-slate-300">Dosyayı sürükleyin veya tıklayın</div>
                <div className="text-xs text-slate-500 mt-1">JPEG, PNG, WebP • Maks 10 MB</div>
              </>
            )}
          </label>

          <div className="mt-5 space-y-3 max-h-[55vh] overflow-y-auto">
            {receipts.length === 0 && <div className="text-sm text-slate-500">Henüz fiş yüklenmedi.</div>}
            {receipts.map((r) => {
              const o = r.ocr || {};
              return (
                <div key={r.id} className="p-3 rounded-lg bg-slate-900/40 border border-slate-800">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest font-mono-num text-slate-500">{o.document_type || "fiş"}</div>
                    <div className="text-xs text-slate-500 font-mono-num">{(o.confidence * 100 || 0).toFixed(0)}%</div>
                  </div>
                  <div className="font-head font-semibold mt-1 truncate">{o.seller_name || "Bilinmeyen Satıcı"}</div>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-slate-400">{o.date || "—"} • {o.category_suggestion || "—"}</span>
                    <span className="font-mono-num font-semibold text-emerald-400">{o.total ? fmtTRY(o.total) : "—"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
